import { Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ageGateApplies, ageGateAnswered } from "./lib/ageGate";
import { isNativeApp } from "./lib/platform";
import { isAuthCallbackUrl } from './lib/authHandoff';
import AuthHandoffListener from './components/AuthHandoffListener';
import { pingFirstOpen } from "./lib/metrics";
import { liveFirstRunVariant, restoreFirstRunDone } from "./lib/firstRun";
import "./lib/seoPaths"; // captures SSR_BOOT_PATH before the first render replaces the server's #root
import { lazyWithReload } from "./lib/appUpdate";
import AppErrorBoundary from "./components/AppErrorBoundary";
import UpdateWatcher from "./components/UpdateWatcher";
import "./App.css";
import type { ReactNode } from "react";

// Lazy-loaded pages (code splitting); a chunk gone after a deploy reloads the page once (lib/appUpdate.ts)
const HomePage = lazyWithReload(() => import(/* webpackChunkName: "home" */ "./pages/HomePage"));
const CreateStoryPage = lazyWithReload(() => import(/* webpackChunkName: "create" */ "./pages/CreateStoryPage"));
const BookReaderPage = lazyWithReload(() => import(/* webpackChunkName: "reader" */ "./pages/BookReaderPage"));
const SettingsPage = lazyWithReload(() => import(/* webpackChunkName: "settings" */ "./pages/SettingsPage"));
const SharedReaderPage = lazyWithReload(() => import(/* webpackChunkName: "shared-reader" */ "./pages/SharedReaderPage"));
const PublicLibraryPage = lazyWithReload(() => import(/* webpackChunkName: "library" */ "./pages/PublicLibraryPage"));
const PurchasePage = lazyWithReload(() => import(/* webpackChunkName: "purchase" */ "./pages/PurchasePage"));
const DashboardPage = lazyWithReload(() => import(/* webpackChunkName: "dashboard" */ "./pages/DashboardPage"));
const GiftSentPage = lazyWithReload(() => import(/* webpackChunkName: "gift-sent" */ "./pages/GiftSentPage"));
const VerifyEmailPage = lazyWithReload(() => import(/* webpackChunkName: "verify-email" */ "./pages/VerifyEmailPage"));
const ForgotPasswordPage = lazyWithReload(() => import(/* webpackChunkName: "forgot-password" */ "./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazyWithReload(() => import(/* webpackChunkName: "reset-password" */ "./pages/ResetPasswordPage"));
const TryStoryPage = lazyWithReload(() => import(/* webpackChunkName: "try" */ "./pages/TryStoryPage"));
const AuthCallbackPage = lazyWithReload(() => import(/* webpackChunkName: "auth-callback" */ "./pages/AuthCallbackPage"));
const NativeReturnPage = lazyWithReload(() => import(/* webpackChunkName: "native-return" */ './pages/NativeReturnPage'));
const AppBridgePage = lazyWithReload(() => import(/* webpackChunkName: "app-bridge" */ "./pages/AppBridgePage"));
const AdminPage = lazyWithReload(() => import(/* webpackChunkName: "admin" */ "./pages/AdminPage"));
// Seed story search pages (2026-09-30, backend/seo.py): the server renders /<lang>/<section>/… for crawlers.
const SeoStoryPage = lazyWithReload(() => import(/* webpackChunkName: "seo-story" */ "./pages/SeoStoryPage"));
const SeoIndexPage = lazyWithReload(() => import(/* webpackChunkName: "seo-index" */ "./pages/SeoIndexPage"));
// Login / Register / the Play age screen were eager until 2026-09-05; Register
// pulled Radix Select + floating-ui (~20 KB gz) into the main bundle for every
// visitor, including the ad landings that never see a form.
const LoginPage = lazyWithReload(() => import(/* webpackChunkName: "login" */ "./pages/LoginPage"));
const RegisterPage = lazyWithReload(() => import(/* webpackChunkName: "register" */ "./pages/RegisterPage"));
const AgeGatePage = lazyWithReload(() => import(/* webpackChunkName: "age-gate" */ "./pages/AgeGatePage"));
// Story-first native first run (2026-10-02, docs/NATIVE-FIRST-RUN-2026-10.md): its own chunk, only the shells load it.
const FirstRunPage = lazyWithReload(() => import(/* webpackChunkName: "first-run" */ "./pages/FirstRunPage"));

// The search pages are dark like the reader: a white spinner between the server's page and the reader would flash.
const DarkSpinner = () => (
  <div className="min-h-screen bg-[#1A1B26] flex items-center justify-center">
    <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const LoadingSpinner = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg text-muted-foreground font-heading">Laden...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Come back here after signing in (2026-09-10, Kai: /admin bounced to the
    // home page). LoginPage/RegisterPage read ?next= through safeNext().
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  return <>{children}</>;
};

// Landing: logged-in users get their home, anonymous visitors the public
// library. A login wall as the first impression killed campaign traffic —
// every cold link to app.snugtale.com hit a form instead of the product.
// A fresh signed-out native install gets the story-first first run instead (2026-10-02, lib/firstRunCore.ts:
// within 24 h of first_open, no trial on the device, not done; Android after the age screen).
const LandingRoute = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  if (!user && liveFirstRunVariant(false) !== null) return <Navigate to="/welcome" replace />;
  return user ? <HomePage /> : <PublicLibraryPage />;
};

// Public Route (redirect to home if logged in)
// Same rule as LoginPage/RegisterPage's safeNext(): an in-app path only.
function safeNextParam(search: string): string | null {
  const raw = new URLSearchParams(search).get("next");
  return raw && raw.startsWith("/") && !raw.startsWith("//") ? raw : null;
}

const PublicRoute = ({ children }: { children: ReactNode }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (user) {
    // A signed-in visitor on /login or /register goes where they were headed
    // (?next=, set by ProtectedRoute) — this wrapper used to win the race
    // against LoginPage's own navigate(next) and bounce /admin to the home page.
    return <Navigate to={safeNextParam(location.search) || "/"} replace />;
  }

  return <>{children}</>;
};

function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();

  // A new route opens at the top (2026-09-10): the SPA kept the previous page's
  // scroll offset, so a parent who tapped "Sichern" under a story landed on the
  // register page scrolled 147 px down with the logo and title cut off. Browser
  // back/forward (POP) keeps its position — the library list must not jump.
  useEffect(() => {
    if (navigationType !== "POP") window.scrollTo(0, 0);
  }, [location.pathname, navigationType]);

  useEffect(() => {
    // Deep links only exist in the shells; on the web the eager listener used to
    // cost one wasted plugin chunk request at boot.
    if (!isNativeApp()) return;
    let cancelled = false;
    let handle: { remove: () => Promise<void> } | undefined;
    let backHandle: { remove: () => Promise<void> } | undefined;
    import("@capacitor/app")
      .then(async ({ App: CapApp }) => {
        // Android system back: previous in-app screen when there is one, else leave
        // the app (registering a listener replaces Capacitor's default, so both
        // branches are spelled out). iOS has no system back — see BackButton.
        backHandle = await CapApp.addListener("backButton", ({ canGoBack }) => {
          if (canGoBack) window.history.back();
          else CapApp.exitApp();
        });
        if (cancelled) { backHandle.remove(); backHandle = undefined; }
        return CapApp.addListener("appUrlOpen", ({ url }) => {
          // snugtale://auth/callback?code=… — the system-browser Facebook login coming back (2026-09-18):
          // AuthHandoffListener (inside AuthProvider) redeems it; never treat it as a path.
          if (isAuthCallbackUrl(url)) return;
          try {
            const path = new URL(url).pathname;
            if (path) navigate(path);
          } catch { /* ignore malformed URLs */ }
        });
      })
      .then((h) => { if (cancelled) h.remove(); else handle = h; })
      .catch(() => { /* plugin unavailable */ });
    return () => { cancelled = true; handle?.remove(); backHandle?.remove(); };
  }, [navigate]);

  // Google Play Families age gate — Android native only. Redirect to
  // /age-check on first launch until the user has answered. Applies
  // across all routes except the gate itself.
  if (ageGateApplies() && !ageGateAnswered() && location.pathname !== '/age-check') {
    return <Navigate to="/age-check" replace />;
  }

  return (
    <Suspense fallback={<LoadingSpinner />}>
      {/* Native system-browser login return (snugtale://auth/callback) — needs the Router and AuthProvider. */}
      <AuthHandoffListener />
      <Routes>
        <Route path="/age-check" element={<AgeGatePage />} />
        {/* Native first run (the page re-checks who gets it and sends everyone else home) */}
        <Route path="/welcome" element={<Suspense fallback={<DarkSpinner />}><FirstRunPage /></Suspense>} />
        {/* Public routes - no auth required */}
        <Route path="/shared/:shareToken" element={<SharedReaderPage />} />
        <Route path="/library" element={<PublicLibraryPage />} />
        {/* Search pages; the pages check lang + section against lib/seoPaths.ts and send anything else home. */}
        <Route path="/:lang/:section/:slug" element={<Suspense fallback={<DarkSpinner />}><SeoStoryPage /></Suspense>} />
        <Route path="/:lang/:section" element={<Suspense fallback={<DarkSpinner />}><SeoIndexPage /></Suspense>} />
        {/* Anonymous trial story (web); the page itself sends logged-in users to /create */}
        <Route path="/try" element={<TryStoryPage />} />
        {/* Web Google Sign-In lands here with tokens in the URL fragment — bare route,
            NOT PublicRoute (that would bounce to / and drop `next`) */}
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/auth/native-return" element={<NativeReturnPage />} />
        {/* Web → app bridge (docs/WEB-TO-APP-BRIDGE-2026-09.md): store redirect with
            attribution. Bare route — logged-in users arrive here from /purchase, /settings
            and the reader, logged-out ones from emails. */}
        <Route path="/app" element={<AppBridgePage />} />

        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/forgot-password" element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        } />
        <Route path="/login" element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } />
        <Route path="/register" element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        } />
        <Route path="/" element={<LandingRoute />} />
        <Route path="/create" element={
          <ProtectedRoute>
            <CreateStoryPage />
          </ProtectedRoute>
        } />
        <Route path="/story/:storyId" element={
          <ProtectedRoute>
            <BookReaderPage />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        } />
        <Route path="/purchase" element={
          <ProtectedRoute>
            <PurchasePage />
          </ProtectedRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } />
        <Route path="/gift-sent/:storyId" element={
          <ProtectedRoute>
            <GiftSentPage />
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    pingFirstOpen();
    // The first run's one-shot flag back from @capacitor/preferences after an iOS storage eviction (lib/firstRun.ts).
    void restoreFirstRunDone();
  }, []);
  return (
    <AppErrorBoundary>
      <AuthProvider>
        <BrowserRouter basename={process.env.PUBLIC_URL || ""}>
          <div className="min-h-screen bg-background">
            <AppRoutes />
            <UpdateWatcher />
            <Toaster position="top-center" richColors />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </AppErrorBoundary>
  );
}

export default App;
